
import React from "react";
import {
  Edit3,
  Eye,
  GripVertical,
  Trash2,
  FileQuestion,
} from "lucide-react";

/**
 * EnglishQuestionGroupQuestionList
 *
 * Displays the questions belonging to an English Question Group.
 *
 * Props:
 * - questions: Array of question objects
 * - loading: Loading state
 * - onView: Callback when viewing a question
 * - onEdit: Callback when editing a question
 * - onRemove: Callback when removing a question from the group
 * - showActions: Whether to display action buttons
 * - showDragHandle: Whether to display drag handles
 */
const EnglishQuestionGroupQuestionList = ({
  questions = [],
  loading = false,
  onView,
  onEdit,
  onRemove,
  showActions = true,
  showDragHandle = false,
}) => {
  // ======================================================
  // Loading State
  // ======================================================

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-5 py-4">
          <div className="h-5 w-48 animate-pulse rounded bg-gray-200" />
        </div>

        <div className="divide-y divide-gray-100">
          {[1, 2, 3].map((item) => (
            <div
              key={item}
              className="flex gap-4 p-5"
            >
              <div className="h-8 w-8 shrink-0 animate-pulse rounded-lg bg-gray-200" />

              <div className="min-w-0 flex-1 space-y-3">
                <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-gray-100" />

                <div className="flex gap-2">
                  <div className="h-6 w-20 animate-pulse rounded-full bg-gray-100" />
                  <div className="h-6 w-20 animate-pulse rounded-full bg-gray-100" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ======================================================
  // Empty State
  // ======================================================

  if (!questions.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
          <FileQuestion className="h-6 w-6 text-gray-400" />
        </div>

        <h3 className="mt-4 text-sm font-semibold text-gray-900">
          No questions found
        </h3>

        <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">
          There are currently no English questions associated with this
          question group.
        </p>
      </div>
    );
  }

  // ======================================================
  // Helpers
  // ======================================================

  const getQuestionId = (question) =>
    question?._id || question?.id;

  const getQuestionText = (question) =>
    question?.questionText ||
    question?.text ||
    "Question text unavailable";

  const getQuestionTypeLabel = (type) => {
    if (!type) return "Unknown";

    return type
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (char) => char.toUpperCase());
  };

  const getDifficultyClasses = (difficulty) => {
    switch (difficulty) {
      case "easy":
        return "bg-green-50 text-green-700 ring-green-600/20";

      case "medium":
        return "bg-yellow-50 text-yellow-700 ring-yellow-600/20";

      case "hard":
        return "bg-red-50 text-red-700 ring-red-600/20";

      default:
        return "bg-gray-50 text-gray-600 ring-gray-500/20";
    }
  };

  // ======================================================
  // Render
  // ======================================================

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Header */}
      <div className="flex flex-col gap-3 border-b border-gray-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Group Questions
          </h2>

          <p className="mt-0.5 text-sm text-gray-500">
            {questions.length}{" "}
            {questions.length === 1
              ? "question"
              : "questions"}{" "}
            in this group
          </p>
        </div>
      </div>

      {/* Questions */}
      <div className="divide-y divide-gray-100">
        {questions.map((question, index) => {
          const questionId = getQuestionId(question);

          return (
            <div
              key={questionId || index}
              className="group flex gap-3 px-4 py-5 transition hover:bg-gray-50 sm:gap-4 sm:px-5"
            >
              {/* Drag Handle */}
              {showDragHandle && (
                <div className="hidden shrink-0 cursor-grab items-center text-gray-400 sm:flex">
                  <GripVertical className="h-5 w-5" />
                </div>
              )}

              {/* Number */}
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-sm font-semibold text-blue-700">
                {index + 1}
              </div>

              {/* Question Content */}
              <div className="min-w-0 flex-1">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="whitespace-pre-wrap text-sm font-medium leading-6 text-gray-900">
                      {getQuestionText(question)}
                    </p>

                    {/* Question Metadata */}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {question?.questionType && (
                        <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                          {getQuestionTypeLabel(
                            question.questionType
                          )}
                        </span>
                      )}

                      {question?.difficulty && (
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${getDifficultyClasses(
                            question.difficulty
                          )}`}
                        >
                          {getQuestionTypeLabel(
                            question.difficulty
                          )}
                        </span>
                      )}

                      {question?.marks !== undefined && (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                          {question.marks}{" "}
                          {Number(question.marks) === 1
                            ? "mark"
                            : "marks"}
                        </span>
                      )}

                      {question?.negativeMarks !==
                        undefined &&
                        Number(question.negativeMarks) >
                          0 && (
                          <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700">
                            -{question.negativeMarks}
                          </span>
                        )}

                      {question?.isActive !==
                        undefined && (
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${
                            question.isActive
                              ? "bg-green-50 text-green-700"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {question.isActive
                            ? "Active"
                            : "Inactive"}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  {showActions && (
                    <div className="flex shrink-0 items-center gap-1">
                      {onView && (
                        <button
                          type="button"
                          onClick={() =>
                            onView(question)
                          }
                          title="View question"
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-blue-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      )}

                      {onEdit && (
                        <button
                          type="button"
                          onClick={() =>
                            onEdit(question)
                          }
                          title="Edit question"
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-indigo-50 hover:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                      )}

                      {onRemove && (
                        <button
                          type="button"
                          onClick={() =>
                            onRemove(question)
                          }
                          title="Remove question"
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Options Preview */}
                {Array.isArray(question?.options) &&
                  question.options.length > 0 && (
                    <div className="mt-4 grid gap-2 sm:grid-cols-2">
                      {question.options
                        .slice(0, 4)
                        .map((option, optionIndex) => {
                          const optionText =
                            typeof option ===
                            "object"
                              ? option?.text ||
                                option?.label ||
                                option?.value ||
                                ""
                              : option;

                          return (
                            <div
                              key={
                                optionIndex
                              }
                              className="flex min-w-0 items-start gap-2 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
                            >
                              <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-semibold text-gray-500 shadow-sm">
                                {String.fromCharCode(
                                  65 +
                                    optionIndex
                                )}
                              </span>

                              <span className="min-w-0 break-words text-xs leading-5 text-gray-600">
                                {optionText}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  )}

                {/* More Options */}
                {Array.isArray(question?.options) &&
                  question.options.length > 4 && (
                    <p className="mt-2 text-xs text-gray-400">
                      +{" "}
                      {question.options.length -
                        4}{" "}
                      more options
                    </p>
                  )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EnglishQuestionGroupQuestionList;

